import {
  EVENT_LISTENER_METHODS,
  eventBus,
  leaveSession,
  showVirtualBackgroundDialog,
  startScreenSharing,
  stopScreenSharing,
  toggleRaiseHand,
  toggleSettingsDialog,
  useBaseStore,
  useConfigStore,
  useHideScreenSharingButton,
  useRaisedHandTimestamp,
  useScreenShareRunning,
  useTranslation,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../icon/Icon.web';
import AudioControl from './controls/Audio.web';
import VideoControl from './controls/Video.web';
import MoreMenu from './MoreMenu.web';
import Settings from './Settings.web';
import './style.css';
import VirtualBackground from './VirtualBackground.web';

const ControlPane: React.FC = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const settingsDialogVisible = useBaseStore(
    settingsDialogVisible => settingsDialogVisible.settingsDialogVisible,
  );
  const [isMobileView, setIsMobileView] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const { t } = useTranslation();

  const isScreenShareRunning = useScreenShareRunning();

  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // Monitor main-content width for dynamic mobile view
  useEffect(() => {
    const mainContentElement = document.querySelector(
      '.cometchat-calls-control-pane',
    ) as HTMLElement;

    if (!mainContentElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsMobileView(width < 767);
        setViewportWidth(width);
      }
    });

    resizeObserver.observe(mainContentElement);

    // Initial check
    const initialWidth = mainContentElement.offsetWidth;
    setIsMobileView(initialWidth < 767);
    setViewportWidth(initialWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const toggleMoreMenuState = () => {
    setIsMoreMenuOpen(prev => !prev);
  };

  const raiseHandTimestamp = useRaisedHandTimestamp();
  const shouldHideButtons = useCallback(() => {
    return viewportWidth < 480;
  }, [viewportWidth]);

  const hideControlPanel = useConfigStore(state => state.hideControlPanel);
  const hideScreenSharingButton = useHideScreenSharingButton();
  const hideLeaveSessionButton = useConfigStore(
    state => state.hideLeaveSessionButton,
  );

  const onLeaveSessionClick = useCallback(() => {
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.ButtonClickListener
        .onLeaveSessionButtonClicked,
    });
    leaveSession();
  }, []);

  const onToggleRaiseHandClick = useCallback(() => {
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.ButtonClickListener.onRaiseHandButtonClicked,
    });
    toggleRaiseHand();
  }, []);

  if (hideControlPanel) {
    return null;
  }

  return (
    <div
      className={clsx('cometchat-calls-control-pane', {
        'cometchat-calls-control-pane--mobile': isMobileView,
      })}>
      <AudioControl />
      <VideoControl />

      <button
        className={clsx(
          'cometchat-calls-control-button',
          'cometchat-calls-control-button--raise-hand',
          {
            'cometchat-calls-control-button--active': raiseHandTimestamp,
            'cometchat-calls-control-button--hidden': shouldHideButtons(),
          },
        )}
        onClick={onToggleRaiseHandClick}
        aria-label={
          raiseHandTimestamp
            ? t('controlPanel.lowerHand')
            : t('controlPanel.raiseHand')
        }
        title={
          raiseHandTimestamp
            ? t('controlPanel.lowerHand')
            : t('controlPanel.raiseHand')
        }>
        <Icon
          name="raise-hand-fill"
          fill={`${raiseHandTimestamp ? 'var(--cometchat-calls-static-black)' : 'var(--cometchat-calls-static-white)'}`}
        />
      </button>

      {!hideScreenSharingButton && (
        <button
          className={clsx(
            'cometchat-calls-control-button',
            'cometchat-calls-control-button--screen-share',
            {
              'cometchat-calls-control-button--hidden': shouldHideButtons(),
              'cometchat-calls-control-button--active': isScreenShareRunning,
            },
          )}
          onClick={
            isScreenShareRunning ? stopScreenSharing : startScreenSharing
          }
          aria-label={
            isScreenShareRunning
              ? t('controlPanel.stopScreenSharing')
              : t('controlPanel.startScreenSharing')
          }
          title={
            isScreenShareRunning
              ? t('controlPanel.stopScreenSharing')
              : t('controlPanel.startScreenSharing')
          }>
          <Icon
            name={isScreenShareRunning ? 'screen-share-stop' : 'screen-share'}
            fill="var(--cometchat-calls-static-white)"
          />
        </button>
      )}

      <button
        ref={moreButtonRef}
        className={clsx(
          'cometchat-calls-control-button',
          'cometchat-calls-control-button--more',
          {
            'cometchat-calls-control-button--active': isMoreMenuOpen,
          },
        )}
        onClick={toggleMoreMenuState}
        data-testid="cometchat-more-options-button"
        aria-label={t('controlPanel.moreOptions')}
        title={t('controlPanel.moreOptions')}>
        <Icon name="more" fill="var(--cometchat-calls-static-white)" />
      </button>

      {!hideLeaveSessionButton && (
        <button
          className="cometchat-calls-control-button cometchat-calls-control-button--call-end"
          onClick={onLeaveSessionClick}
          aria-label={t('controlPanel.leaveSession')}
          title={t('controlPanel.leaveSession')}>
          <Icon name="call-end" fill="var(--cometchat-calls-static-white)" />
        </button>
      )}
      <Settings
        visible={settingsDialogVisible}
        onClose={toggleSettingsDialog}
      />
      <VirtualBackground />
      <MoreMenu
        toggleSettings={toggleSettingsDialog}
        toggleVirtualBackground={showVirtualBackgroundDialog}
        isVisible={isMoreMenuOpen}
        onClose={toggleMoreMenuState}
        anchorElement={moreButtonRef.current}
        isMobileView={shouldHideButtons()}
      />
    </div>
  );
};

export default ControlPane;
